import React, { useContext } from 'react';
import { useSelector } from 'react-redux';
import { produce } from 'immer';
import { SBadge } from '@chargebee/sting-react';
import { FunnelPlacementContext } from '@bbkAdminComponents/experiences/offers/funnel-placement-context';
import {
  generateGenericSelectOptions,
  type GroupedMultiSelectOption,
  type GroupedMultiSelectOptions,
  type SelectOption,
} from '@bbkAdminComponents/select/select';
import { selectAppId } from '@bbkAdminRedux/app/selectors';
import { useGetSelectedBrand } from '@bbkAdminRedux/rtkq/brands.slice';
import { useSelectControlFunnel } from '@bbkAdminRedux/rtkq/funnels.slice';
import {
  useSelectActiveLossAversions,
  useSelectActiveOffers,
  useSelectLossAversionCompatibleEntities,
} from '@bbkAdminRedux/rtkq/offers-and-lossAversions.slice';
import { createLossAversionPlacement } from '@bbkAdminRedux/rtkq/placements.slice';
import {
  isPlacementForLossAversion,
  type PlacementLossAversionPosition,
} from '@bbkAdminRedux/rtkq/placements.utils';
import { isLossAversion } from '@bbkAdminUtils/api-client/rest-cards-types';
import { FeatureFlagsE, useFeatureFlags } from '@bbkAdminUtils/feature-flags';
import { GlobalLogger } from '@bbkAdminUtils/GlobalLogger';
import { type ReactFCC } from '@bbkAdminUtils/utility-types';
import { PageEditorSelect } from './design-element/page-editor-select';
import { FunnelContext } from './FunnelContext';

export const FormatLASelectDropdown: React.FC<
  GroupedMultiSelectOption<SelectOption<string | undefined>> & {
    data?: { shared: boolean };
  }
> = (props) => (
  <div>
    <span>{props?.label}</span>
    <span className="tw-ml-2">
      {props?.data?.shared && (
        <SBadge variant="primary" mode="light" className="tw-mx-1">
          Shared
        </SBadge>
      )}
    </span>
  </div>
);

type Props = {
  slot: PlacementLossAversionPosition;
  disabled?: boolean;
};

export const LossAversionCardSelector: ReactFCC<Props> = ({
  slot,
  disabled,
}) => {
  const appKey = useSelector(selectAppId);
  const { funnelProps } = useContext(FunnelContext);
  const funnelId = +funnelProps!.funnelId;
  const brandId = useGetSelectedBrand(appKey);
  const isMultiBrand = useFeatureFlags(FeatureFlagsE.ADDITIONAL_BRANDS_ENABLED);
  const activeOffers = useSelectActiveOffers({
    appKey,
    brandId: isMultiBrand ? brandId : undefined,
  });
  const { funnelPlacement, setFunnelPlacement, setLossAversionErrors } =
    useContext(FunnelPlacementContext);
  const contentId = funnelPlacement?.placements
    .filter(isPlacementForLossAversion)
    .find((p) => p.position === slot)?.content_id;

  const controlFunnel = useSelectControlFunnel({ appKey, brandId });
  const otherLaContentIds = funnelPlacement?.placements
    .filter(isPlacementForLossAversion)
    .filter((p) => p.position !== slot)
    .map((p) => !!p && p.content_id);

  const isControlPage = controlFunnel?.id === +funnelId;
  const isContentAlreadyPlaced = (contentId: string) =>
    !!otherLaContentIds && otherLaContentIds.includes(contentId);

  const lossAversionCompatibleEntities =
    useSelectLossAversionCompatibleEntities({ appKey });
  const offerOrLa = contentId
    ? lossAversionCompatibleEntities[contentId]
    : undefined;
  const defaultValue = {
    value: offerOrLa?.id || undefined,
    label: offerOrLa?.display_name || 'Select a Loss Aversion card',
  };

  const lossAversionsWithoutOffers = useSelectActiveLossAversions({
    appKey,
    brandId: isMultiBrand ? brandId : undefined,
  });
  const lossAversionsWithOffers = activeOffers.filter(isLossAversion);
  const lossAversionOptions: GroupedMultiSelectOptions<
    SelectOption<string | undefined> & { data?: { shared: boolean } }
  > = [
    {
      label: 'Select a Loss Aversion card',
      value: undefined,
    },
    ...(!isControlPage && lossAversionsWithOffers.length
      ? [
          {
            label: 'Cards with offers',
            options: generateGenericSelectOptions(
              lossAversionsWithOffers || [],
              (la) => ({ shared: la.shared })
            ).map((x) => ({
              ...x,
              isDisabled: isContentAlreadyPlaced(x.value),
            })),
          },
        ]
      : []),
    {
      label: 'Cards without offers',
      options: generateGenericSelectOptions(
        lossAversionsWithoutOffers || [],
        (la) => ({ shared: la.shared })
      ).map((x) => ({
        ...x,
        isDisabled: isContentAlreadyPlaced(x.value),
      })),
    },
  ];

  const onPlacementChange = (laOpt?: { value: string | undefined }) => {
    if (!funnelPlacement) return;

    const updatedPlacement = produce(funnelPlacement, (draft) => {
      if (!draft) return;
      let draftLAPlacement = draft.placements
        .filter(isPlacementForLossAversion)
        .find((p) => p.position === slot);

      if (!draftLAPlacement) {
        GlobalLogger.warn(
          'Could not find loss aversion placement ',
          slot,
          funnelPlacement
        );
        draftLAPlacement = createLossAversionPlacement({
          position: slot,
          funnel_id: funnelId,
        });
        draft.placements.push(draftLAPlacement);
      }

      draftLAPlacement.content_id = laOpt?.value;
    });
    setFunnelPlacement(updatedPlacement);
    setLossAversionErrors([]);
  };

  return (
    <PageEditorSelect
      searchable={false}
      disabled={disabled}
      options={lossAversionOptions}
      onChange={(laOpt) => {
        onPlacementChange(laOpt);
      }}
      value={defaultValue}
      placeholder="Select a Loss Aversion card"
      renderOption={isMultiBrand ? FormatLASelectDropdown : undefined}
    />
  );
};
